import { useTranslation } from "react-i18next";
import { ObjectRefContainer } from "../../../../../common/ObjectRefContainer";
import type { EditorValue } from "../../../../../services/api/instances/editorApi/types";
import { AppUnderlinedTitle } from "../../../../../shared/components/AppUnderlinedTitle";
import { SpaNameChangeModal } from "../../SpaNameChangeModal";
import { SpaLinkEditor } from "../SpaLinkEditor";
import { SpaLinkPublish } from "../SpaLinkSettings/SpaLinkPublish";
import { StorageObjectVersion } from "../../../../../services/api/instances/storageApi/types/object_types";
import { VersioningServiceManager } from "../../../../../common/VersioningServiceManager";
import { useStorageObjectVersions } from "../../../../../app/state/storageObjectSlice/hook/use-storage-object-versions";
import { RenameObjectInput } from "../../../../../common/RenameObjectInput";
import { useStorageSelectors } from "../../../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { SaveVersionModal } from "../../../../../common/VersioningServiceManager/VersioningBlockModals/SaveVersionModal";
import { RestoreVersionModal } from "../../../../../common/VersioningServiceManager/VersioningBlockModals/RestoreVersionsModal";
import { setSpaValue } from "../../../redux/slice";
import { useAppModals } from "../../../../../app/state/modalSlice/hook/use-app-modals";
import { useAppSelector } from "../../../../../services/redux/hooks/use-selector";
import { selectSpaUriObject } from "../../../redux/selectors";
import styles from "./styles.module.css";
import { useUpdateStorageValue } from "../../../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { ClearButton } from "../../../../../common/Components/Buttons/ClearButton";
import { AppButton } from "../../../../../shared/components/AppButton";
import { useAppMenus } from "../../../../../app/state/menuSlice/hook/use-app-menus";
import { SaveIcon } from "../../../../../shared/icons/SaveIcon";
import { AttachedHtmlToSpaMenu } from "./AttachedHtmlToSpaMenu";

interface Props {
  handleClearChat: () => void;
}

export const HtmlEditorHeader = ({ handleClearChat }: Props) => {
  const {
    versions,
    selectedVersion,
    createObjectVersion,
    updateObjectVersion,
    restoreObjectVersion,
    refreshVersion,
    deleteObjectVersion,
  } = useStorageObjectVersions("ai_agents_spa");
  const { selectedObject } = useStorageSelectors("ai_agents_spa");
  const spaUriObject = useAppSelector(selectSpaUriObject);
  const { handleCloseModal, handleOpenModal } = useAppModals();
  const { handleCloseMenu, handleOpenMenu } = useAppMenus();
  const { saveValue } = useUpdateStorageValue("ai_agents_spa", setSpaValue);
  const { t } = useTranslation();

  return (
    <>
      <AppUnderlinedTitle
        leftContent={
          <RenameObjectInput service={"ai_agents_spa"} inputType={"input"} />
        }
      >
        <AppButton
          buttonType="text"
          onClick={(e) => handleOpenMenu(e, "attached_html_to_spa", null)}
          icon={<SaveIcon />}
        >
          Загрузить HTML
        </AppButton>
      </AppUnderlinedTitle>
      <div className={styles["editor-header"]}>
        <div className={styles["left-header-block"]}>
          <ObjectRefContainer refValue={selectedObject?.ref ?? ""} />
          <VersioningServiceManager
            service={"ai_agents_spa"}
            versions={versions ? versions[selectedObject?.id as string] : []}
            selectedVersion={
              selectedVersion as StorageObjectVersion<EditorValue>
            }
            onEditName={updateObjectVersion}
            onRefreshVersion={() =>
              refreshVersion(selectedVersion?.version_id as string)
            }
            onOpenSaveVersionModal={() =>
              handleOpenModal("save_version_modal", null)
            }
            onOpenRestoreVersionModal={() =>
              handleOpenModal("restore_version_modal", null)
            }
            onDeleteVersion={deleteObjectVersion}
            isSaveButtonDisabled={false}
          />
        </div>
        <div className={styles["right-header-block"]}>
          <SpaLinkEditor
            onOpenChangeSpaNameModal={() =>
              handleOpenModal("spa_name_change_modal", null)
            }
            id={selectedObject?.id as string}
          />
          <SpaLinkPublish
            saveSpa={saveValue}
            isDisabled={
              spaUriObject?.uri?.relative_uri === "" ||
              spaUriObject?.uri?.relative_uri === undefined
            }
          />
        </div>
      </div>
      <SaveVersionModal
        selectedVersion={selectedVersion as StorageObjectVersion<EditorValue>}
        createVersion={createObjectVersion}
        closeModal={handleCloseModal}
      />
      <RestoreVersionModal
        versions={versions ? versions[selectedObject?.id as string] : []}
        closeModal={handleCloseModal}
        onOk={(id) => restoreObjectVersion(id, setSpaValue)}
      />
      <SpaNameChangeModal
        saveSpa={saveValue}
        closeModal={handleCloseModal}
        uri={spaUriObject?.uri?.module as string}
        id={selectedObject?.id as string}
      />
      <AttachedHtmlToSpaMenu closeMenu={handleCloseMenu} />
    </>
  );
};
